import React, { useState } from "react";
import { Plus, Trash2, Upload, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { uploadImage } from "../lib/r2";
import { toast } from "sonner";

export const Field = ({ label, children, hint, testId }) => (
    <div className="space-y-2" data-testid={testId}>
        <Label className="text-xs tracking-[0.18em] uppercase text-slate-600">
            {label}
        </Label>
        {children}
        {hint && <p className="text-xs text-slate-500">{hint}</p>}
    </div>
);

export const TextField = ({ label, value, onChange, hint, testId, type = "text" }) => (
    <Field label={label} hint={hint} testId={testId}>
        <Input
            type={type}
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            className="bg-white"
        />
    </Field>
);

export const TextAreaField = ({ label, value, onChange, hint, rows = 4, testId }) => (
    <Field label={label} hint={hint} testId={testId}>
        <Textarea
            rows={rows}
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            className="bg-white"
        />
    </Field>
);

export const ImageField = ({ label, value, onChange, testId }) => {
    const [busy, setBusy] = useState(false);
    const handleUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setBusy(true);
        try {
            const url = await uploadImage(file);
            onChange(url);
            toast.success("Şəkil yükləndi");
        } catch (err) {
            console.error(err);
            toast.error("Şəkil yüklənmədi");
        } finally {
            setBusy(false);
        }
    };
    return (
        <Field label={label} testId={testId}>
            <div className="flex items-start gap-4">
                <div className="w-32 h-24 bg-slate-100 border border-slate-200 overflow-hidden flex-shrink-0">
                    {value ? (
                        <img
                            src={value}
                            alt=""
                            className="w-full h-full object-cover"
                        />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center text-slate-400 text-xs">
                            şəkil yoxdur
                        </div>
                    )}
                </div>
                <div className="flex-1 space-y-2">
                    <Input
                        value={value || ""}
                        onChange={(e) => onChange(e.target.value)}
                        placeholder="https://..."
                        className="bg-white text-xs"
                        data-testid={`${testId}-url`}
                    />
                    <label className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-slate-200 hover:bg-slate-50 cursor-pointer text-xs">
                        {busy ? (
                            <Loader2 size={14} className="animate-spin" />
                        ) : (
                            <Upload size={14} />
                        )}
                        Şəkil yüklə
                        <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={handleUpload}
                            disabled={busy}
                            data-testid={`${testId}-file`}
                        />
                    </label>
                </div>
            </div>
        </Field>
    );
};

export const ListEditor = ({ items, onChange, renderItem, addLabel, newItem, testId }) => {
    return (
        <div className="space-y-4" data-testid={testId}>
            {(items || []).map((item, idx) => (
                <div
                    key={item.id || idx}
                    className="bg-white border border-slate-200 p-5 space-y-4 relative"
                >
                    <div className="flex items-center justify-between">
                        <div className="text-xs tracking-[0.2em] uppercase text-slate-500">
                            #{idx + 1}
                        </div>
                        <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                                const next = [...items];
                                next.splice(idx, 1);
                                onChange(next);
                            }}
                            data-testid={`${testId}-remove-${idx}`}
                        >
                            <Trash2 size={14} />
                        </Button>
                    </div>
                    {renderItem(item, (patched) => {
                        const next = [...items];
                        next[idx] = { ...item, ...patched };
                        onChange(next);
                    }, idx)}
                </div>
            ))}
            <Button
                type="button"
                variant="outline"
                onClick={() =>
                    onChange([
                        ...(items || []),
                        { ...newItem(), id: `n${Date.now()}` },
                    ])
                }
                data-testid={`${testId}-add`}
                className="w-full"
            >
                <Plus size={14} className="mr-2" />
                {addLabel}
            </Button>
        </div>
    );
};
